import React, { useState, useEffect, useRef } from 'react';
import * as XLSX from 'xlsx';
import { ArrowLeft, TrendingUp, Activity, Lock, Plus, Minus, UserPlus, UserMinus, FileDown } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import LogoLoader from '../components/LogoLoader';

const ALLOWED_EMAIL = 'alameengoldandsilver@gmail.com';

const ACTION_OPTIONS = [
  { value: 'amount_added',   label: 'Amount Added' },
  { value: 'amount_updated', label: 'Amount Updated' },
  { value: 'amount_deleted', label: 'Amount Deleted' },
  { value: 'user_created',   label: 'Member Added' },
  { value: 'user_updated',   label: 'Member Updated' },
  { value: 'user_deleted',   label: 'Member Deleted' },
];

function MultiSelect({ options, selected, onChange, placeholder = 'All' }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);
  const toggle = (val) => onChange(selected.includes(val) ? selected.filter(v => v !== val) : [...selected, val]);
  const displayLabel = selected.length === 0 ? placeholder
    : selected.length === 1 ? (options.find(o => o.value === selected[0])?.label || selected[0])
    : `${selected.length} selected`;
  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button type="button" onClick={() => setOpen(v => !v)} style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px',
        padding: '9px 14px', borderRadius: '10px',
        border: `1.5px solid ${selected.length ? '#C9A84C' : '#e2e8f0'}`,
        fontSize: '14px', color: selected.length ? '#0f172a' : '#94a3b8',
        background: selected.length ? '#fef9ec' : '#f8fafc',
        cursor: 'pointer', outline: 'none', minWidth: '180px',
        fontWeight: selected.length ? '600' : '400',
      }}>
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{displayLabel}</span>
        <span style={{ fontSize: '10px', color: '#94a3b8', flexShrink: 0 }}>{open ? '▲' : '▼'}</span>
      </button>
      {open && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 4px)', left: 0, zIndex: 1000,
          background: '#fff', border: '1.5px solid #e2e8f0', borderRadius: '10px',
          boxShadow: '0 8px 24px rgba(0,0,0,0.12)', minWidth: '100%',
          padding: '6px 0',
        }}>
          {options.map(opt => {
            const checked = selected.includes(opt.value);
            return (
              <label key={opt.value} style={{
                display: 'flex', alignItems: 'center', gap: '10px',
                padding: '8px 14px', cursor: 'pointer', fontSize: '13px',
                color: '#334155', background: checked ? '#fef9ec' : 'transparent',
                fontWeight: checked ? '600' : '400',
              }}>
                <input type="checkbox" checked={checked} onChange={() => toggle(opt.value)}
                  style={{ accentColor: '#C9A84C', width: '15px', height: '15px', cursor: 'pointer' }} />
                {opt.label}
              </label>
            );
          })}
        </div>
      )}
    </div>
  );
}

const s = {
  page: { height: '100vh', overflow: 'hidden', background: '#faf7f0', fontFamily: "'Segoe UI', sans-serif", display: 'flex', flexDirection: 'column' },
  navbar: {
    background: 'linear-gradient(135deg, #0a0a0a, #1a1a1a)',
    padding: '0 20px', height: '64px',
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    boxShadow: '0 2px 20px rgba(0,0,0,0.3)', flexShrink: 0,
    position: 'sticky', top: 0, zIndex: 100,
  },
  navBrand: { display: 'flex', alignItems: 'center', gap: '10px' },
  navTitle: { color: '#fff', fontSize: '18px', fontWeight: '800', margin: 0 },
  backBtn: {
    display: 'flex', alignItems: 'center', gap: '6px',
    background: 'rgba(255,255,255,0.1)', color: '#cbd5e1',
    border: '1px solid rgba(255,255,255,0.15)',
    padding: '8px 14px', borderRadius: '8px',
    fontSize: '13px', fontWeight: '600', cursor: 'pointer',
  },
  content: {  padding: '32px 24px', width: '100%' },
  th: { padding: '12px 16px', textAlign: 'left', fontSize: '11px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', background: '#f8fafc', letterSpacing: '0.5px', whiteSpace: 'nowrap', position: 'sticky', top: 0, zIndex: 1 },
  td: { padding: '12px 16px', borderBottom: '1px solid #f1f5f9', fontSize: '13px', color: '#334155', verticalAlign: 'middle', whiteSpace: 'nowrap' },
};

function StatCard({ icon, label, value, sub, color }) {
  return (
    <div className="gs-stat-card-mobile" style={{ background: '#fff', borderRadius: '14px', padding: '18px 20px', boxShadow: '0 1px 8px rgba(0,0,0,0.07)', borderLeft: `4px solid ${color}` }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
        <div className="gs-sc-icon" style={{ width: '36px', height: '36px', borderRadius: '10px', background: color + '18', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          {icon}
        </div>
        <p className="gs-sc-label" style={{ margin: 0, fontSize: '11px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px', lineHeight: 1.3 }}>{label}</p>
      </div>
      <p className="gs-sc-value" style={{ margin: 0, fontSize: '22px', fontWeight: '800', color: '#0f172a' }}>{value}</p>
      {sub && <p style={{ margin: '3px 0 0', fontSize: '11px', color: '#94a3b8' }}>{sub}</p>}
    </div>
  );
}

export default function AdminActivity() {
  const [loading, setLoading] = useState(true);
  const [allowed, setAllowed] = useState(null);
  const [txLogs, setTxLogs] = useState([]);
  const [amounts, setAmounts] = useState([]);
  const [selectedAdmin, setSelectedAdmin] = useState('');
  const [selectedActions, setSelectedActions] = useState([]);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session?.user?.email?.toLowerCase() !== ALLOWED_EMAIL) {
        setAllowed(false);
        setLoading(false);
      } else {
        setAllowed(true);
        loadData();
      }
    });
  }, []);

  const loadData = async () => {
    setLoading(true);
    const [{ data: txData }, { data: amtData }] = await Promise.all([
      supabase.from('transaction_logs').select('*').order('created_at', { ascending: false }),
      supabase.from('amounts').select('id, amount, gram, added_by, date, users(saving_type)'),
    ]);
    setTxLogs(txData || []);
    setAmounts((amtData || []).map(a => ({ ...a, saving_type: a.users?.saving_type || '' })));
    setLoading(false);
  };

  // ── Date range filter ──────────────────────────────────────────────────────
  const inRange = (dateStr) => {
    if (!dateStr) return true;
    const dt = new Date(dateStr);
    if (startDate && dt < new Date(startDate + 'T00:00:00')) return false;
    if (endDate && dt > new Date(endDate + 'T23:59:59')) return false;
    return true;
  };

  // ── Collect all known admin names ──────────────────────────────────────────
  const allAdmins = [...new Set([
    ...txLogs.map(t => t.performed_by).filter(Boolean),
    ...amounts.map(a => a.added_by).filter(Boolean),
  ])].sort();

  // ── Deduplicate set for legacy amounts not yet in tx_logs ─────────────────
  const txAmtAddedIds = new Set(txLogs.filter(t => t.event_type === 'amount_added').map(t => t.amount_id).filter(Boolean));

  // ── Compute stats per admin ────────────────────────────────────────────────
  const adminList = selectedAdmin ? [selectedAdmin] : allAdmins;

  const adminStats = adminList.map(admin => {
    // Users added via tx_logs (event logged as 'user_created' in Dashboard)
    const usersAdded = txLogs.filter(t =>
      (t.event_type === 'user_added' || t.event_type === 'user_created') && t.performed_by === admin && inRange(t.created_at)
    ).length;

    // Users deleted: tx_logs only
    const usersDeleted = txLogs.filter(t =>
      t.event_type === 'user_deleted' && t.performed_by === admin && inRange(t.created_at)
    ).length;

    // Amount added: tx_logs (amount_added) + legacy amounts not in tx_logs + delta from updates
    const txAmountAdded = txLogs
      .filter(t => t.event_type === 'amount_added' && t.performed_by === admin && inRange(t.created_at))
      .reduce((s, t) => s + Number(t.amount || 0), 0);
    const legacyAmountAdded = amounts
      .filter(a => a.added_by === admin && !txAmtAddedIds.has(a.id) && inRange(a.date + 'T00:00:00'))
      .reduce((s, a) => s + Number(a.amount || 0), 0);
    const txAmountUpdatedDelta = txLogs
      .filter(t => t.event_type === 'amount_updated' && t.performed_by === admin && inRange(t.created_at))
      .reduce((s, t) => s + (Number(t.amount || 0) - Number(t.old_data?.amount || 0)), 0);
    const totalAmountAdded = txAmountAdded + legacyAmountAdded + txAmountUpdatedDelta;

    // Amount deleted: individual amount_deleted events
    // Build set of user_ids that have per-amount logs (newer deletions via Dashboard fix)
    const userIdsWithAmtDeleted = new Set(
      txLogs.filter(t => t.event_type === 'amount_deleted' && t.performed_by === admin && inRange(t.created_at))
        .map(t => t.user_id).filter(Boolean)
    );
    const txAmountDeleted = txLogs
      .filter(t => t.event_type === 'amount_deleted' && t.performed_by === admin && inRange(t.created_at))
      .reduce((s, t) => s + Number(t.amount || t.old_data?.amount || 0), 0);
    // Fallback: user_deleted events for historical members with no per-amount logs
    const legacyUserDelAmt = txLogs
      .filter(t => t.event_type === 'user_deleted' && t.performed_by === admin && !userIdsWithAmtDeleted.has(t.user_id) && inRange(t.created_at))
      .reduce((s, t) => s + Number(t.amount || t.old_data?.live_balance || 0), 0);
    const totalAmountDeleted = txAmountDeleted + legacyUserDelAmt;

    // Gold gram added
    const goldGramAdded = txLogs
      .filter(t => t.event_type === 'amount_added' && t.performed_by === admin && t.saving_type === 'gold' && inRange(t.created_at))
      .reduce((s, t) => s + Number(t.gram || t.new_data?.gram || 0), 0);
    const legacyGoldGramAdded = amounts
      .filter(a => a.added_by === admin && a.saving_type === 'gold' && !txAmtAddedIds.has(a.id) && inRange(a.date + 'T00:00:00'))
      .reduce((s, a) => s + Number(a.gram || 0), 0);
    const goldGramUpdatedDelta = txLogs
      .filter(t => t.event_type === 'amount_updated' && t.performed_by === admin && t.saving_type === 'gold' && inRange(t.created_at))
      .reduce((s, t) => s + (Number(t.gram || 0) - Number(t.old_data?.gram || 0)), 0);
    const totalGoldGramAdded = goldGramAdded + legacyGoldGramAdded + goldGramUpdatedDelta;

    // Silver gram added
    const silverGramAdded = txLogs
      .filter(t => t.event_type === 'amount_added' && t.performed_by === admin && t.saving_type === 'silver' && inRange(t.created_at))
      .reduce((s, t) => s + Number(t.gram || t.new_data?.gram || 0), 0);
    const legacySilverGramAdded = amounts
      .filter(a => a.added_by === admin && a.saving_type === 'silver' && !txAmtAddedIds.has(a.id) && inRange(a.date + 'T00:00:00'))
      .reduce((s, a) => s + Number(a.gram || 0), 0);
    const silverGramUpdatedDelta = txLogs
      .filter(t => t.event_type === 'amount_updated' && t.performed_by === admin && t.saving_type === 'silver' && inRange(t.created_at))
      .reduce((s, t) => s + (Number(t.gram || 0) - Number(t.old_data?.gram || 0)), 0);
    const totalSilverGramAdded = silverGramAdded + legacySilverGramAdded + silverGramUpdatedDelta;

    // Gold gram deleted: amount_deleted events + user_deleted fallback for historical
    const txGoldGramDeleted = txLogs
      .filter(t => t.event_type === 'amount_deleted' && t.performed_by === admin && t.saving_type === 'gold' && inRange(t.created_at))
      .reduce((s, t) => s + Number(t.gram || t.old_data?.gram || 0), 0);
    const legacyUserDelGoldGram = txLogs
      .filter(t => t.event_type === 'user_deleted' && t.performed_by === admin && t.saving_type === 'gold' && !userIdsWithAmtDeleted.has(t.user_id) && inRange(t.created_at))
      .reduce((s, t) => s + Number(t.gram || t.old_data?.total_gram || 0), 0);
    const totalGoldGramDeleted = txGoldGramDeleted + legacyUserDelGoldGram;

    // Silver gram deleted: amount_deleted events + user_deleted fallback for historical
    const txSilverGramDeleted = txLogs
      .filter(t => t.event_type === 'amount_deleted' && t.performed_by === admin && t.saving_type === 'silver' && inRange(t.created_at))
      .reduce((s, t) => s + Number(t.gram || t.old_data?.gram || 0), 0);
    const legacyUserDelSilverGram = txLogs
      .filter(t => t.event_type === 'user_deleted' && t.performed_by === admin && t.saving_type === 'silver' && !userIdsWithAmtDeleted.has(t.user_id) && inRange(t.created_at))
      .reduce((s, t) => s + Number(t.gram || t.old_data?.total_gram || 0), 0);
    const totalSilverGramDeleted = txSilverGramDeleted + legacyUserDelSilverGram;

    // Total transactions (amount_added + amount_updated + amount_deleted)
    const txnsCount = txLogs.filter(t =>
      t.event_type === 'amount_added' &&
      t.performed_by === admin && inRange(t.created_at)
    ).length;

    const moneyAmountAdded = txLogs
      .filter(t => t.event_type === 'amount_added' && t.performed_by === admin && t.saving_type === 'money' && inRange(t.created_at))
      .reduce((s, t) => s + Number(t.amount || 0), 0);
    const legacyMoneyAdded = amounts
      .filter(a => a.added_by === admin && a.saving_type === 'money' && !txAmtAddedIds.has(a.id) && inRange(a.date + 'T00:00:00'))
      .reduce((s, a) => s + Number(a.amount || 0), 0);
    const moneyAmountUpdatedDelta = txLogs
      .filter(t => t.event_type === 'amount_updated' && t.performed_by === admin && t.saving_type === 'money' && inRange(t.created_at))
      .reduce((s, t) => s + (Number(t.amount || 0) - Number(t.old_data?.amount || 0)), 0);
    const totalMoneyAdded = moneyAmountAdded + legacyMoneyAdded + moneyAmountUpdatedDelta;

    return { admin, usersAdded, usersDeleted, totalAmountAdded, totalAmountDeleted, totalGoldGramAdded, totalSilverGramAdded, totalGoldGramDeleted, totalSilverGramDeleted, totalMoneyAdded,
    totalMoneyDeleted: txLogs
      .filter(t => t.event_type === 'amount_deleted' && t.performed_by === admin && t.saving_type === 'money' && inRange(t.created_at))
      .reduce((s, t) => s + Number(t.amount || t.old_data?.amount || 0), 0) + txLogs.filter(t => t.event_type === 'user_deleted' && t.performed_by === admin && t.saving_type === 'money' && !userIdsWithAmtDeleted.has(t.user_id) && inRange(t.created_at)).reduce((s, t) => s + Number(t.amount || t.old_data?.live_balance || 0), 0),
    txnsCount };
  }).filter(s => s.totalAmountAdded + s.totalAmountDeleted + s.txnsCount + s.usersAdded + s.usersDeleted > 0);

  // ── Activity log (recent actions) ─────────────────────────────────────────
  const activityLog = txLogs.filter(t =>
    (!selectedAdmin || t.performed_by === selectedAdmin) &&
    (selectedActions.length === 0 || selectedActions.includes(t.event_type)) &&
    inRange(t.created_at)
  ).slice(0, 300);

  // ── Overall totals ─────────────────────────────────────────────────────────
  // All totals computed directly from txLogs to avoid adminStats filter dropping any data
  const af = t => (!selectedAdmin || t.performed_by === selectedAdmin) && inRange(t.created_at);
  const txAmtAddedIdsAll = new Set(txLogs.filter(t => t.event_type === 'amount_added').map(t => t.amount_id).filter(Boolean));

  const totalUsersAdded = txLogs.filter(t => (t.event_type === 'user_added' || t.event_type === 'user_created') && af(t)).length;
  const totalUsersDeleted = txLogs.filter(t => t.event_type === 'user_deleted' && af(t)).length;

  const _txAmtAdded = txLogs.filter(t => t.event_type === 'amount_added' && af(t)).reduce((s, t) => s + Number(t.amount || 0), 0);
  const _legacyAmtAdded = amounts.filter(a => (!selectedAdmin || a.added_by === selectedAdmin) && !txAmtAddedIdsAll.has(a.id) && inRange(a.date + 'T00:00:00')).reduce((s, a) => s + Number(a.amount || 0), 0);
  const _updatedDelta = txLogs.filter(t => t.event_type === 'amount_updated' && af(t)).reduce((s, t) => s + (Number(t.amount || 0) - Number(t.old_data?.amount || 0)), 0);
  const totalAmountAddedAll = _txAmtAdded + _legacyAmtAdded + _updatedDelta;

  const _userIdsWithAmtDel = new Set(txLogs.filter(t => t.event_type === 'amount_deleted' && af(t)).map(t => t.user_id).filter(Boolean));
  const _txAmtDeleted = txLogs.filter(t => t.event_type === 'amount_deleted' && af(t)).reduce((s, t) => s + Number(t.amount || t.old_data?.amount || 0), 0);
  const _legacyUserDelAmt = txLogs.filter(t => t.event_type === 'user_deleted' && af(t) && !_userIdsWithAmtDel.has(t.user_id)).reduce((s, t) => s + Number(t.amount || t.old_data?.live_balance || 0), 0);
  const totalAmountDeletedAll = _txAmtDeleted + _legacyUserDelAmt;

  const totalStats = adminStats.reduce(
    (acc, s) => ({
      amountAdded: acc.amountAdded,
      amountDeleted: acc.amountDeleted,
      goldGram: acc.goldGram + s.totalGoldGramAdded,
      silverGram: acc.silverGram + s.totalSilverGramAdded,
      goldGramDeleted: acc.goldGramDeleted + s.totalGoldGramDeleted,
      silverGramDeleted: acc.silverGramDeleted + s.totalSilverGramDeleted,
      moneyAdded: acc.moneyAdded + s.totalMoneyAdded,
      moneyDeleted: acc.moneyDeleted + s.totalMoneyDeleted,
      txns: acc.txns + s.txnsCount,
      usersAdded: acc.usersAdded,
      usersDeleted: acc.usersDeleted,
    }),
    { amountAdded: totalAmountAddedAll, amountDeleted: totalAmountDeletedAll, goldGram: 0, silverGram: 0, goldGramDeleted: 0, silverGramDeleted: 0, moneyAdded: 0, moneyDeleted: 0, txns: 0, usersAdded: totalUsersAdded, usersDeleted: totalUsersDeleted }
  );

  const isFiltered = !!(selectedAdmin || selectedActions.length || startDate || endDate);

  const downloadExcel = () => {
    // Sheet 1: Summary
    const summaryRows = [
      { Metric: 'Members Added', Value: totalStats.usersAdded },
      { Metric: 'Members Deleted', Value: totalStats.usersDeleted },
      { Metric: 'Net Amount (₹)', Value: totalStats.amountAdded - totalStats.amountDeleted },
      { Metric: 'Amount Added (₹)', Value: totalStats.amountAdded },
      { Metric: 'Amount Deleted (₹)', Value: totalStats.amountDeleted },
      { Metric: 'Gold Gram Added (g)', Value: totalStats.goldGram },
      { Metric: 'Gold Gram Deleted (g)', Value: totalStats.goldGramDeleted },
      { Metric: 'Silver Gram Added (g)', Value: totalStats.silverGram },
      { Metric: 'Silver Gram Deleted (g)', Value: totalStats.silverGramDeleted },
      { Metric: 'Money Added (₹)', Value: totalStats.moneyAdded },
      { Metric: 'Money Deleted (₹)', Value: totalStats.moneyDeleted },
      { Metric: 'Total Transactions', Value: totalStats.txns },
    ];
    const wsSummary = XLSX.utils.json_to_sheet(summaryRows);
    // Auto-fit columns for Summary sheet
    wsSummary['!cols'] = [
      { wch: Math.max(20, ...summaryRows.map(r => String(r.Metric).length)) },
      { wch: Math.max(10, ...summaryRows.map(r => String(r.Value).length)) },
    ];

    // Sheet 2: Activity Log
    const logRows = activityLog.map(log => {
      let changes = '';
      if (log.event_type === 'amount_updated') {
        const oldAmt = Number(log.old_data?.amount || 0);
        const newAmt = Number(log.amount || log.new_data?.amount || 0);
        const oldGram = Number(log.old_data?.gram || 0);
        const newGram = Number(log.gram || log.new_data?.gram || 0);
        if (oldAmt !== newAmt) changes += `₹${oldAmt} → ₹${newAmt}  `;
        if (Math.abs(oldGram - newGram) > 0.0001) changes += `${oldGram.toFixed(3)}g → ${newGram.toFixed(3)}g`;
        if (log.old_data?.date && log.new_data?.date && log.old_data.date !== log.new_data.date)
          changes += `  Date: ${log.old_data.date} → ${log.new_data.date}`;
      } else if (log.event_type === 'user_updated') {
        if (log.old_data?.name !== log.new_data?.name) changes += `Name: ${log.old_data?.name} → ${log.new_data?.name}  `;
        if (log.old_data?.phone !== log.new_data?.phone) changes += `Phone: ${log.old_data?.phone} → ${log.new_data?.phone}  `;
        if (log.old_data?.id !== log.new_data?.id) changes += `ID: ${log.old_data?.id} → ${log.new_data?.id}`;
      }
      return {
        'Date & Time': log.created_at ? new Date(log.created_at).toLocaleString('en-IN') : '',
        'Performed By': log.performed_by || '',
        'Action': eventLabel(log.event_type).label,
        'Member': log.member_name ? `${log.member_name}${log.user_id ? ` (${log.user_id})` : ''}` : '',
        'Type': log.saving_type || '',
        'Amount (₹)': log.amount ? Number(log.amount) : '',
        'Gram': (log.gram || log.new_data?.gram) ? Number(log.gram || log.new_data?.gram) : '',
        'Changes': changes || '',
        'Note': log.note || log.new_data?.note || '',
      };
    });
    const wsLog = XLSX.utils.json_to_sheet(logRows);
    // Auto-fit columns for Activity Log sheet
    const logHeaders = ['Date & Time', 'Performed By', 'Action', 'Member', 'Type', 'Amount (₹)', 'Gram', 'Changes', 'Note'];
    wsLog['!cols'] = logHeaders.map(h => ({
      wch: Math.max(
        h.length,
        ...logRows.map(r => String(r[h] ?? '').length)
      ) + 2,
    }));

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, wsSummary, 'Summary');
    XLSX.utils.book_append_sheet(wb, wsLog, 'Activity Log');
    const suffix = selectedAdmin ? `_${selectedAdmin}` : '';
    const dateRange = (startDate || endDate) ? `_${startDate || ''}to${endDate || ''}` : '';
    XLSX.writeFile(wb, `StaffActivity${suffix}${dateRange}.xlsx`);
  };

  const eventLabel = (type) => {
    if (type === 'amount_added') return { label: 'Amount Added', color: '#10b981', bg: '#ecfdf5' };
    if (type === 'amount_updated') return { label: 'Amount Updated', color: '#3b82f6', bg: '#eff6ff' };
    if (type === 'amount_deleted') return { label: 'Amount Deleted', color: '#ef4444', bg: '#fef2f2' };
    if (type === 'user_added' || type === 'user_created') return { label: 'Member Added', color: '#8b5cf6', bg: '#f5f3ff' };
    if (type === 'user_updated') return { label: 'Member Updated', color: '#3b82f6', bg: '#eff6ff' };
    if (type === 'user_deleted') return { label: 'Member Deleted', color: '#f59e0b', bg: '#fffbeb' };
    return { label: type, color: '#64748b', bg: '#f8fafc' };
  };

  // ─── Access Denied ─────────────────────────────────────────────────────────
  if (allowed === null || loading) return <LogoLoader />;

  if (allowed === false) return (
    <div style={{ minHeight: '100vh', background: '#faf7f0', fontFamily: "'Segoe UI', sans-serif", display: 'flex', flexDirection: 'column' }}>
      <nav style={s.navbar}>
        <div style={s.navBrand}>
          <div style={{ width: '42px', height: '42px', borderRadius: '50%', overflow: 'hidden', cursor: 'pointer' }} onClick={() => window.location.reload()}>
            <img src="/logo.jpg" alt="Al Ameen" style={{ width: '42px', height: '42px', objectFit: 'cover', borderRadius: '50%' }} />
          </div>
          <p style={s.navTitle}>Al Ameen Gold & Silver</p>
        </div>
        <button onClick={() => navigate('/dashboard')} style={s.backBtn}><ArrowLeft size={15} /> Back</button>
      </nav>
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px' }}>
        <div style={{ background: '#fff', borderRadius: '20px', padding: '48px 40px', maxWidth: '420px', width: '100%', textAlign: 'center', boxShadow: '0 8px 32px rgba(0,0,0,0.1)' }}>
          <div style={{ width: '72px', height: '72px', borderRadius: '50%', background: 'rgba(239,68,68,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px' }}>
            <Lock size={34} color="#ef4444" />
          </div>
          <h2 style={{ margin: '0 0 10px', fontSize: '22px', fontWeight: '800', color: '#0f172a' }}>Access Denied</h2>
          <p style={{ margin: '0 0 28px', fontSize: '15px', color: '#64748b' }}>This page is restricted to authorised administrators only.</p>
          <button onClick={() => navigate('/dashboard')} style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', background: 'linear-gradient(135deg, #0a0a0a, #1a1a1a)', color: '#fff', border: 'none', padding: '12px 24px', borderRadius: '10px', fontSize: '14px', fontWeight: '700', cursor: 'pointer' }}>
            <ArrowLeft size={16} /> Go to Dashboard
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div style={s.page}>
      {/* ── Navbar ── */}
      <nav style={s.navbar}>
        <div style={s.navBrand}>
          <div style={{ width: '42px', height: '42px', borderRadius: '50%', overflow: 'hidden', cursor: 'pointer' }} onClick={() => window.location.reload()}>
            <img src="/logo.jpg" alt="Al Ameen" style={{ width: '42px', height: '42px', objectFit: 'cover', borderRadius: '50%', display: 'block' }} />
          </div>
          <p className="gs-aa-title" style={s.navTitle}>Al Ameen Gold & Silver</p>
        </div>
        <button onClick={() => navigate('/admin')} style={s.backBtn}><ArrowLeft size={15} /> Admin</button>
      </nav>

      <div style={{ flex: 1, overflowY: 'auto' }}>
        <div style={s.content}>

          {/* ── Header ── */}
          <div className="gs-aa-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '14px', marginBottom: '24px', flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
              <div style={{ width: '48px', height: '48px', background: 'linear-gradient(135deg, #6366f1, #4f46e5)', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <Activity size={22} color="#fff" />
              </div>
              <div>
                <h1 style={{ margin: 0, fontSize: '20px', fontWeight: '800', color: '#0f172a' }}>Staff Activity Report</h1>
                <p style={{ margin: '3px 0 0', fontSize: '13px', color: '#64748b' }}>See who added / deleted members & amounts</p>
              </div>
            </div>
            <div>
            <button
              onClick={downloadExcel}
              style={{ display: 'flex', alignItems: 'center', gap: '8px', background: 'linear-gradient(135deg, #C9A84C, #9A7A2C)', color: '#fff', border: 'none', padding: '11px 20px', borderRadius: '10px', fontSize: '14px', fontWeight: '700', cursor: 'pointer', boxShadow: '0 4px 12px rgba(201,168,76,0.3)', whiteSpace: 'nowrap' }}
            >
              <FileDown size={16} /> Download Report
            </button>
            </div>
          </div>

          {/* ── Filters ── */}
          <div className="gs-aa-filters" style={{ background: '#fff', borderRadius: '14px', padding: '16px 20px', marginBottom: '20px', boxShadow: '0 1px 8px rgba(0,0,0,0.07)', display: 'flex', flexWrap: 'wrap', gap: '14px', alignItems: 'flex-end', position: 'relative', zIndex: 10 }}>
            <div>
              <p style={{ margin: '0 0 6px', fontSize: '11px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase' }}>Staff / Admin</p>
              <select
                value={selectedAdmin}
                onChange={e => setSelectedAdmin(e.target.value)}
                style={{ padding: '9px 14px', borderRadius: '10px', border: '1.5px solid #e2e8f0', fontSize: '14px', background: selectedAdmin ? '#fef9ec' : '#f8fafc', color: selectedAdmin ? '#0f172a' : '#94a3b8', cursor: 'pointer', outline: 'none', minWidth: '180px', fontWeight: selectedAdmin ? '600' : '400', borderColor: selectedAdmin ? '#C9A84C' : '#e2e8f0' }}
              >
                <option value="">All Staff</option>
                {allAdmins.map(a => <option key={a} value={a}>{a}</option>)}
              </select>
            </div>
            <div>
              <p style={{ margin: '0 0 6px', fontSize: '11px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase' }}>From Date</p>
              <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} style={{ padding: '9px 12px', borderRadius: '10px', border: '1.5px solid #e2e8f0', fontSize: '13px', background: '#f8fafc', color: '#334155', outline: 'none' }} />
            </div>
            <div>
              <p style={{ margin: '0 0 6px', fontSize: '11px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase' }}>To Date</p>
              <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} style={{ padding: '9px 12px', borderRadius: '10px', border: '1.5px solid #e2e8f0', fontSize: '13px', background: '#f8fafc', color: '#334155', outline: 'none' }} />
            </div>
            <div>
              <p style={{ margin: '0 0 6px', fontSize: '11px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase' }}>Action</p>
              <MultiSelect
                options={ACTION_OPTIONS}
                selected={selectedActions}
                onChange={setSelectedActions}
                placeholder="All Actions"
              />
            </div>
            {isFiltered && (
              <button
                onClick={() => { setSelectedAdmin(''); setSelectedActions([]); setStartDate(''); setEndDate(''); }}
                style={{ padding: '9px 16px', borderRadius: '10px', border: '1px solid #fca5a5', background: '#fef2f2', color: '#ef4444', fontSize: '13px', fontWeight: '700', cursor: 'pointer' }}
              >
                Clear
              </button>
            )}
          </div>

          {/* ── Overall Summary Cards ── */}
          {/* Row 1: Overall Amount Added, Overall Amount Deleted, Members Added, Members Deleted, Transactions */}
          <div className="gs-aa-stats-row1 gs-stats-5col" style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '12px', marginBottom: '12px' }}>
            <StatCard icon={<Plus size={18} color="#10b981" />} label="Overall Amount Added" value={`₹${totalStats.amountAdded.toLocaleString('en-IN')}`} color="#10b981" sub={isFiltered ? 'filtered' : 'All Time'} />
            <StatCard icon={<Minus size={18} color="#f59e0b" />} label="Overall Amount Deleted" value={`₹${totalStats.amountDeleted.toLocaleString('en-IN')}`} color="#f59e0b" sub={isFiltered ? 'filtered' : 'All Time'} />
            <StatCard icon={<UserPlus size={18} color="#8b5cf6" />} label="Members Added" value={totalStats.usersAdded} color="#8b5cf6" sub={isFiltered ? 'filtered' : 'All Time'} />
            <StatCard icon={<UserMinus size={18} color="#ef4444" />} label="Members Deleted" value={totalStats.usersDeleted} color="#ef4444" sub={isFiltered ? 'filtered' : 'All Time'} />
            <StatCard icon={<Activity size={18} color="#3b82f6" />} label="Transactions" value={totalStats.txns} color="#3b82f6" sub={isFiltered ? 'filtered' : 'All Time'} />
          </div>
          {/* Row 2: Gold/Silver/Money gram cards */}
          <div className="gs-aa-stats-row2 gs-stats-6col" style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: '12px', marginBottom: '24px' }}>
            <StatCard icon={<TrendingUp size={18} color="#D97706" />} label="Gold Gram Added" value={`${totalStats.goldGram.toFixed(3)} g`} color="#D97706" sub={isFiltered ? 'filtered' : 'All Time'} />
            <StatCard icon={<Minus size={18} color="#ef4444" />} label="Gold Gram Deleted" value={`${totalStats.goldGramDeleted.toFixed(3)} g`} color="#ef4444" sub={isFiltered ? 'filtered' : 'All Time'} />
            <StatCard icon={<TrendingUp size={18} color="#475569" />} label="Silver Gram Added" value={`${totalStats.silverGram.toFixed(3)} g`} color="#475569" sub={isFiltered ? 'filtered' : 'All Time'} />
            <StatCard icon={<Minus size={18} color="#94a3b8" />} label="Silver Gram Deleted" value={`${totalStats.silverGramDeleted.toFixed(3)} g`} color="#94a3b8" sub={isFiltered ? 'filtered' : 'All Time'} />
            <StatCard icon={<Plus size={18} color="#16a34a" />} label="Money Added" value={`₹${totalStats.moneyAdded.toLocaleString('en-IN')}`} color="#16a34a" sub={isFiltered ? 'filtered' : 'All Time'} />
            <StatCard icon={<Minus size={18} color="#dc2626" />} label="Money Deleted" value={`₹${totalStats.moneyDeleted.toLocaleString('en-IN')}`} color="#dc2626" sub={isFiltered ? 'filtered' : 'All Time'} />
          </div>

          {/* ── Activity Log Table ── */}
          <h2 style={{ margin: '0 0 12px', fontSize: '15px', fontWeight: '800', color: '#0f172a' }}>
            Activity Log <span style={{ fontSize: '12px', fontWeight: '500', color: '#94a3b8' }}>({activityLog.length} Records)</span>
          </h2>
          <div className="gs-aa-table" style={{ background: '#fff', borderRadius: '16px', boxShadow: '0 1px 8px rgba(0,0,0,0.07)', overflow: 'hidden' }}>
            <div style={{ overflowX: 'auto', maxHeight: '500px', overflowY: 'auto' }}>
              {activityLog.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '60px 20px', color: '#94a3b8', fontSize: '15px' }}>
                  <div style={{ fontSize: '36px', marginBottom: '10px' }}>📭</div>
                  No activity found.
                </div>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr>
                      <th style={s.th}>Date & Time</th>
                      <th style={s.th}>Performed By</th>
                      <th style={{ ...s.th, textAlign: 'center' }}>Action</th>
                      <th style={s.th}>Member</th>
                      <th style={{ ...s.th, textAlign: 'center' }}>Type</th>
                      <th style={{ ...s.th, textAlign: 'right' }}>Amount</th>
                      <th style={{ ...s.th, textAlign: 'center' }}>Gram</th>
                      <th style={s.th}>Changes</th>
                      <th style={s.th}>Note</th>
                    </tr>
                  </thead>
                  <tbody>
                    {activityLog.map((log, i) => {
                      const ev = eventLabel(log.event_type);
                      return (
                        <tr key={log.id || i} style={{ background: i % 2 === 0 ? '#fff' : '#fafafa' }}>
                          <td style={{ ...s.td, color: '#64748b', fontSize: '12px', whiteSpace: 'nowrap' }}>
                            {log.created_at ? new Date(log.created_at).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—'}
                          </td>
                          <td style={{ ...s.td, fontWeight: '700', color: '#0f172a' }}>{log.performed_by || '—'}</td>
                          <td style={{ ...s.td, textAlign: 'center' }}>
                            <span style={{ display: 'inline-block', borderRadius: '20px', padding: '3px 10px', fontSize: '11px', fontWeight: '700', background: ev.bg, color: ev.color }}>
                              {ev.label}
                            </span>
                          </td>
                          <td style={{ ...s.td, color: '#334155' }}>
                            {log.member_name
                              ? <>{log.member_name}{log.user_id ? <span style={{ color: '#94a3b8', fontWeight: '400', marginLeft: '4px' }}>({log.user_id})</span> : null}</>
                              : '—'}
                          </td>
                          <td style={{ ...s.td, textAlign: 'center' }}>
                            {log.saving_type === 'gold' && <span style={{ background: '#fef3c7', color: '#D97706', borderRadius: '20px', padding: '2px 10px', fontSize: '12px', fontWeight: '700' }}>Gold</span>}
                            {log.saving_type === 'silver' && <span style={{ background: '#f1f5f9', color: '#475569', borderRadius: '20px', padding: '2px 10px', fontSize: '12px', fontWeight: '700' }}>Silver</span>}
                            {log.saving_type === 'money' && <span style={{ background: '#dcfce7', color: '#16a34a', borderRadius: '20px', padding: '2px 10px', fontSize: '12px', fontWeight: '700' }}>Money</span>}
                            {!log.saving_type && '—'}
                          </td>
                          <td style={{ ...s.td, textAlign: 'center', fontWeight: '700', color: log.amount > 0 ? '#0f172a' : '#94a3b8' }}>
                            {log.amount ? `₹${Number(log.amount).toLocaleString('en-IN')}` : '—'}
                          </td>
                          <td style={{ ...s.td, textAlign: 'center', color: '#D97706', fontWeight: '600' }}>
                            {(log.gram || log.new_data?.gram) ? `${Number(log.gram || log.new_data?.gram).toFixed(3)} g` : '—'}
                          </td>
                          <td style={{ ...s.td, minWidth: '160px', whiteSpace: 'nowrap' }}>
                            {(() => {
                              if (log.event_type === 'amount_updated') {
                                const parts = [];
                                const oldAmt = Number(log.old_data?.amount || 0);
                                const newAmt = Number(log.amount || log.new_data?.amount || 0);
                                const oldGram = Number(log.old_data?.gram || 0);
                                const newGram = Number(log.gram || log.new_data?.gram || 0);
                                const oldDate = log.old_data?.date;
                                const newDate = log.new_data?.date;
                                if (oldAmt !== newAmt)
                                  parts.push(<span key="amt"><span style={{ color: '#ef4444' }}>₹{oldAmt.toLocaleString('en-IN')}</span>{' → '}<span style={{ color: '#10b981', fontWeight: '700' }}>₹{newAmt.toLocaleString('en-IN')}</span></span>);
                                if (Math.abs(oldGram - newGram) > 0.0001)
                                  parts.push(<span key="gram"><span style={{ color: '#ef4444' }}>{oldGram.toFixed(3)}g</span>{' → '}<span style={{ color: '#10b981', fontWeight: '700' }}>{newGram.toFixed(3)}g</span></span>);
                                if (oldDate && newDate && oldDate !== newDate)
                                  parts.push(<span key="date" style={{ color: '#64748b' }}>{oldDate}{' → '}{newDate}</span>);
                                return parts.length > 0
                                  ? <span style={{ fontSize: '12px' }}>{parts.reduce((acc, el, i) => i === 0 ? [el] : [...acc, <span key={`sep${i}`} style={{ color: '#cbd5e1', margin: '0 4px' }}>|</span>, el], [])}</span>
                                  : <span style={{ color: '#94a3b8' }}>—</span>;
                              }
                              if (log.event_type === 'user_updated') {
                                const parts = [];
                                if (log.old_data?.name !== log.new_data?.name)
                                  parts.push(<span key="name"><span style={{ color: '#ef4444' }}>{log.old_data?.name}</span>{' → '}<span style={{ color: '#10b981', fontWeight: '700' }}>{log.new_data?.name}</span></span>);
                                if (log.old_data?.phone !== log.new_data?.phone)
                                  parts.push(<span key="phone"><span style={{ color: '#ef4444' }}>{log.old_data?.phone}</span>{' → '}<span style={{ color: '#10b981', fontWeight: '700' }}>{log.new_data?.phone}</span></span>);
                                if (log.old_data?.id !== log.new_data?.id)
                                  parts.push(<span key="id">ID: <span style={{ color: '#ef4444' }}>{log.old_data?.id}</span>{' → '}<span style={{ color: '#10b981', fontWeight: '700' }}>{log.new_data?.id}</span></span>);
                                return parts.length > 0
                                  ? <span style={{ fontSize: '12px' }}>{parts.reduce((acc, el, i) => i === 0 ? [el] : [...acc, <span key={`sep${i}`} style={{ color: '#cbd5e1', margin: '0 4px' }}>|</span>, el], [])}</span>
                                  : <span style={{ color: '#94a3b8' }}>—</span>;
                              }
                              return <span style={{ color: '#94a3b8' }}>—</span>;
                            })()}
                          </td>
                          <td style={{ ...s.td, color: '#64748b', fontStyle: 'italic', maxWidth: '160px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {log.note || log.new_data?.note || '—'}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
            <div style={{ padding: '10px 18px', background: '#f8fafc', borderTop: '1px solid #f1f5f9', fontSize: '12px', color: '#94a3b8' }}>
              {activityLog.length} {activityLog.length === 1 ? 'Record shown' : 'Records shown'} {isFiltered && <span style={{ color: '#C9A84C', fontWeight: '700' }}>· Filters applied</span>}
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}
